// diff-checker.ts

// ─── Types ────────────────────────────────────────────────────────────────────

type DiffType = 'equal' | 'insert' | 'delete';

interface DiffLine {
    type: DiffType;
    text: string;
}

interface SplitRow {
    leftNum: number | null;
    leftText: string | null;
    leftType: DiffType | 'placeholder';
    rightNum: number | null;
    rightText: string | null;
    rightType: DiffType | 'placeholder';
    charDiff: boolean; // whether to apply char-level highlighting
}

// ─── Myers Diff Algorithm ─────────────────────────────────────────────────────

function computeDiff(originalLines: string[], changedLines: string[]): DiffLine[] {
    const n = originalLines.length;
    const m = changedLines.length;
    const max = n + m;

    if (max === 0) return [];

    const v: Map<number, number> = new Map();
    v.set(1, 0);

    const trace: Map<number, number>[] = [];

    outer: for (let d = 0; d <= max; d++) {
        const snapshot = new Map(v);
        trace.push(snapshot);

        for (let k = -d; k <= d; k += 2) {
            let x: number;
            const down = v.get(k - 1) ?? -1;
            const right = v.get(k + 1) ?? -1;

            if (k === -d || (k !== d && down < right)) {
                x = right;
            } else {
                x = down + 1;
            }

            let y = x - k;

            while (x < n && y < m && originalLines[x] === changedLines[y]) {
                x++;
                y++;
            }

            v.set(k, x);

            if (x >= n && y >= m) {
                trace.push(new Map(v));
                break outer;
            }
        }
    }

    // Backtrack
    const result: DiffLine[] = [];
    let x = n;
    let y = m;

    for (let d = trace.length - 1; d >= 0; d--) {
        const vSnap = trace[d];
        const k = x - y;

        let prevK: number;
        const down = vSnap.get(k - 1) ?? -1;
        const right = vSnap.get(k + 1) ?? -1;

        if (k === -d || (k !== d && down < right)) {
            prevK = k + 1;
        } else {
            prevK = k - 1;
        }

        const prevX = vSnap.get(prevK) ?? 0;
        const prevY = prevX - prevK;

        while (x > prevX + 1 && y > prevY + 1) {
            result.unshift({ type: 'equal', text: originalLines[x - 1] });
            x--;
            y--;
        }

        if (d > 0) {
            if (x === prevX + 1) {
                result.unshift({ type: 'delete', text: originalLines[x - 1] });
                x--;
            } else if (y === prevY + 1) {
                result.unshift({ type: 'insert', text: changedLines[y - 1] });
                y--;
            }
        }

        while (x > prevX && y > prevY) {
            result.unshift({ type: 'equal', text: originalLines[x - 1] });
            x--;
            y--;
        }
    }

    return result;
}

// ─── Similarity score between two strings (0–1) ───────────────────────────────

function similarity(a: string, b: string): number {
    if (a === b) return 1;
    if (a.length === 0 && b.length === 0) return 1;
    if (a.length === 0 || b.length === 0) return 0;

    // Count common characters using a simple LCS length approximation
    const diffs = computeDiff(a.split(''), b.split(''));
    const common = diffs.filter(d => d.type === 'equal').length;
    return (2 * common) / (a.length + b.length);
}

// ─── Character-level diff for changed lines ───────────────────────────────────

function charDiff(a: string, b: string): [string, string] {
    const aChars = a.split('');
    const bChars = b.split('');
    const diffs = computeDiff(aChars, bChars);

    let aHtml = '';
    let bHtml = '';

    for (const d of diffs) {
        const escaped = escapeHtml(d.text);
        if (d.type === 'equal') {
            aHtml += escaped;
            bHtml += escaped;
        } else if (d.type === 'delete') {
            aHtml += `<span class="char-removed">${escaped}</span>`;
        } else {
            bHtml += `<span class="char-added">${escaped}</span>`;
        }
    }

    return [aHtml, bHtml];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function makeLine(
    num: number | null,
    html: string,
    type: DiffType | 'placeholder'
): string {
    const numStr = num !== null ? String(num) : '';
    const cssClass =
        type === 'insert' ? 'diff-added'
            : type === 'delete' ? 'diff-removed'
                : type === 'placeholder' ? 'diff-placeholder'
                    : 'diff-equal';

    const prefix =
        type === 'insert' ? '+&nbsp;'
            : type === 'delete' ? '-&nbsp;'
                : '&nbsp;&nbsp;';

    return `<div class="diff-line ${cssClass}"><span class="diff-line-num">${numStr}</span><span class="diff-line-content">${prefix}${html}</span></div>`;
}

function hunkHeader(msg: string): string {
    return `<div class="diff-hunk-header">${escapeHtml(msg)}</div>`;
}

// ─── Build split-view rows ────────────────────────────────────────────────────
//
// Strategy: collect contiguous blocks of deletes+inserts, then pair them up
// by best similarity match. Unpaired lines get a placeholder on the other side.
// Only apply char-level diff when similarity >= CHAR_DIFF_THRESHOLD.

const CHAR_DIFF_THRESHOLD = 0.3;

function pairBlock(deletes: string[], inserts: string[]): SplitRow[] {
    const rows: SplitRow[] = [];

    if (deletes.length === 0) {
        for (const ins of inserts) {
            rows.push({ leftNum: null, leftText: null, leftType: 'placeholder', rightNum: null, rightText: ins, rightType: 'insert', charDiff: false });
        }
        return rows;
    }

    if (inserts.length === 0) {
        for (const del of deletes) {
            rows.push({ leftNum: null, leftText: del, leftType: 'delete', rightNum: null, rightText: null, rightType: 'placeholder', charDiff: false });
        }
        return rows;
    }

    // Build similarity matrix and greedily pair best matches
    const paired = new Set<number>(); // indices into inserts already claimed
    const leftPaired = new Set<number>(); // indices into deletes already claimed

    // For each delete, find best matching insert
    const bestMatch: Array<{ insertIdx: number; score: number }> = deletes.map((del, di) => {
        let best = -1;
        let bestScore = -1;
        for (let ii = 0; ii < inserts.length; ii++) {
            const s = similarity(del, inserts[ii]);
            if (s > bestScore) {
                bestScore = s;
                best = ii;
            }
        }
        return { insertIdx: best, score: bestScore };
    });

    // Sort deletes by best score descending, pair them off greedily
    const order = deletes.map((_, i) => i).sort((a, b) => bestMatch[b].score - bestMatch[a].score);

    const pairings: Array<{ delIdx: number; insIdx: number; score: number }> = [];
    for (const di of order) {
        const { insertIdx, score } = bestMatch[di];
        if (!paired.has(insertIdx)) {
            pairings.push({ delIdx: di, insIdx: insertIdx, score });
            paired.add(insertIdx);
            leftPaired.add(di);
        }
    }

    // Build a merged sequence preserving original order
    // We interleave: for each delete position, emit the paired insert right after
    const usedInserts = new Set(pairings.map(p => p.insIdx));
    const pairingByDelIdx = new Map(pairings.map(p => [p.delIdx, p]));

    // Emit unpaired inserts that come before any pairing at the top
    const unpairedInserts = inserts
        .map((text, idx) => ({ text, idx }))
        .filter(({ idx }) => !usedInserts.has(idx));

    // Simple approach: emit all paired rows in delete order, then orphan rows
    // Interleave unpaired inserts evenly with unpaired deletes
    const unpairedDeletes = deletes
        .map((text, idx) => ({ text, idx }))
        .filter(({ idx }) => !leftPaired.has(idx));

    // Paired rows first (in original delete order)
    const pairedRows: SplitRow[] = pairings
        .sort((a, b) => a.delIdx - b.delIdx)
        .map(p => ({
            leftNum: null,
            leftText: deletes[p.delIdx],
            leftType: 'delete' as const,
            rightNum: null,
            rightText: inserts[p.insIdx],
            rightType: 'insert' as const,
            charDiff: p.score >= CHAR_DIFF_THRESHOLD,
        }));

    // Orphan deletes (no matching insert)
    const orphanDelRows: SplitRow[] = unpairedDeletes.map(({ text }) => ({
        leftNum: null, leftText: text, leftType: 'delete' as const,
        rightNum: null, rightText: null, rightType: 'placeholder' as const,
        charDiff: false,
    }));

    // Orphan inserts (no matching delete)
    const orphanInsRows: SplitRow[] = unpairedInserts.map(({ text }) => ({
        leftNum: null, leftText: null, leftType: 'placeholder' as const,
        rightNum: null, rightText: text, rightType: 'insert' as const,
        charDiff: false,
    }));

    // Interleave: paired rows, then orphan deletes alongside orphan inserts side by side
    rows.push(...pairedRows);

    const maxOrphan = Math.max(orphanDelRows.length, orphanInsRows.length);
    for (let i = 0; i < maxOrphan; i++) {
        if (i < orphanDelRows.length && i < orphanInsRows.length) {
            // Put them side by side as unmatched changed lines
            rows.push({
                leftNum: null, leftText: orphanDelRows[i].leftText, leftType: 'delete',
                rightNum: null, rightText: orphanInsRows[i].rightText, rightType: 'insert',
                charDiff: false,
            });
        } else if (i < orphanDelRows.length) {
            rows.push(orphanDelRows[i]);
        } else {
            rows.push(orphanInsRows[i]);
        }
    }

    return rows;
}

function buildSplitRows(diffs: DiffLine[]): SplitRow[] {
    const rows: SplitRow[] = [];
    let i = 0;

    while (i < diffs.length) {
        const d = diffs[i];

        if (d.type === 'equal') {
            rows.push({
                leftNum: null, leftText: d.text, leftType: 'equal',
                rightNum: null, rightText: d.text, rightType: 'equal',
                charDiff: false,
            });
            i++;
        } else {
            // Collect the full contiguous block of deletes and inserts
            const blockDeletes: string[] = [];
            const blockInserts: string[] = [];

            while (i < diffs.length && diffs[i].type === 'delete') {
                blockDeletes.push(diffs[i].text);
                i++;
            }
            while (i < diffs.length && diffs[i].type === 'insert') {
                blockInserts.push(diffs[i].text);
                i++;
            }

            rows.push(...pairBlock(blockDeletes, blockInserts));
        }
    }

    // Assign line numbers
    let leftNum = 1;
    let rightNum = 1;
    for (const row of rows) {
        if (row.leftType !== 'placeholder') row.leftNum = leftNum++;
        if (row.rightType !== 'placeholder') row.rightNum = rightNum++;
    }

    return rows;
}

// ─── Render split view ────────────────────────────────────────────────────────

const CONTEXT_LINES = 3;

function renderSplit(rows: SplitRow[]): [string, string] {
    let leftHtml = '';
    let rightHtml = '';

    const isChanged = rows.map(r => r.leftType !== 'equal' || r.rightType !== 'equal');

    const visible = new Set<number>();
    for (let i = 0; i < rows.length; i++) {
        if (isChanged[i]) {
            for (let c = Math.max(0, i - CONTEXT_LINES); c <= Math.min(rows.length - 1, i + CONTEXT_LINES); c++) {
                visible.add(c);
            }
        }
    }

    let lastVisible = -1;
    for (let i = 0; i < rows.length; i++) {
        if (!visible.has(i)) continue;

        if (lastVisible !== -1 && i > lastVisible + 1) {
            const skipped = i - lastVisible - 1;
            const header = hunkHeader(`@@ -${rows[i].leftNum ?? '?'} +${rows[i].rightNum ?? '?'} @@ ${skipped} line${skipped !== 1 ? 's' : ''} skipped`);
            leftHtml += header;
            rightHtml += header;
        }

        const row = rows[i];

        // Left side
        if (row.leftType === 'placeholder') {
            leftHtml += makeLine(null, '', 'placeholder');
        } else {
            let html: string;
            if (row.charDiff && row.rightText !== null) {
                [html] = charDiff(row.leftText!, row.rightText);
            } else {
                html = escapeHtml(row.leftText ?? '');
            }
            leftHtml += makeLine(row.leftNum, html, row.leftType);
        }

        // Right side
        if (row.rightType === 'placeholder') {
            rightHtml += makeLine(null, '', 'placeholder');
        } else {
            let html: string;
            if (row.charDiff && row.leftText !== null) {
                [, html] = charDiff(row.leftText!, row.rightText!);
            } else {
                html = escapeHtml(row.rightText ?? '');
            }
            rightHtml += makeLine(row.rightNum, html, row.rightType);
        }

        lastVisible = i;
    }

    return [leftHtml, rightHtml];
}

// ─── Render unified view ──────────────────────────────────────────────────────

function renderUnified(diffs: DiffLine[]): string {
    let html = '';
    let leftNum = 1;
    let rightNum = 1;

    const isChanged = diffs.map(d => d.type !== 'equal');
    const visible = new Set<number>();
    for (let i = 0; i < diffs.length; i++) {
        if (isChanged[i]) {
            for (let c = Math.max(0, i - CONTEXT_LINES); c <= Math.min(diffs.length - 1, i + CONTEXT_LINES); c++) {
                visible.add(c);
            }
        }
    }

    let lastVisible = -1;
    for (let i = 0; i < diffs.length; i++) {
        const d = diffs[i];

        if (!visible.has(i)) {
            if (d.type === 'equal') { leftNum++; rightNum++; }
            else if (d.type === 'delete') { leftNum++; }
            else { rightNum++; }
            continue;
        }

        if (lastVisible !== -1 && i > lastVisible + 1) {
            const skipped = i - lastVisible - 1;
            html += hunkHeader(`@@ -${leftNum} +${rightNum} @@ ${skipped} line${skipped !== 1 ? 's' : ''} skipped`);
        }

        if (d.type === 'equal') {
            html += makeLine(leftNum, escapeHtml(d.text), 'equal');
            leftNum++;
            rightNum++;
        } else if (d.type === 'delete') {
            html += makeLine(leftNum, escapeHtml(d.text), 'delete');
            leftNum++;
        } else {
            html += makeLine(rightNum, escapeHtml(d.text), 'insert');
            rightNum++;
        }

        lastVisible = i;
    }

    return html;
}

// ─── Stats ────────────────────────────────────────────────────────────────────

interface DiffStats {
    added: number;
    removed: number;
    changed: number;
}

function computeStats(rows: SplitRow[]): DiffStats {
    let added = 0, removed = 0, changed = 0;
    for (const row of rows) {
        const hasLeft = row.leftType !== 'placeholder' && row.leftType !== 'equal';
        const hasRight = row.rightType !== 'placeholder' && row.rightType !== 'equal';
        if (hasLeft && hasRight) changed++;
        else if (hasLeft) removed++;
        else if (hasRight) added++;
    }
    return { added, removed, changed };
}

// ─── DOM wiring ───────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
    const originalEl = document.getElementById('originalText') as HTMLTextAreaElement;
    const changedEl = document.getElementById('changedText') as HTMLTextAreaElement;
    const compareBtn = document.getElementById('compareBtn') as HTMLButtonElement;
    const clearBtn = document.getElementById('clearBtn') as HTMLButtonElement;
    const statsBar = document.getElementById('statsBar') as HTMLElement;
    const statAdded = document.getElementById('statAdded') as HTMLElement;
    const statRemoved = document.getElementById('statRemoved') as HTMLElement;
    const statChanged = document.getElementById('statChanged') as HTMLElement;
    const diffResults = document.getElementById('diffResults') as HTMLElement;
    const emptyState = document.getElementById('emptyState') as HTMLElement;
    const identicalState = document.getElementById('identicalState') as HTMLElement;
    const splitView = document.getElementById('splitView') as HTMLElement;
    const unifiedView = document.getElementById('unifiedView') as HTMLElement;
    const splitLeft = document.getElementById('splitLeft') as HTMLElement;
    const splitRight = document.getElementById('splitRight') as HTMLElement;
    const unifiedContent = document.getElementById('unifiedContent') as HTMLElement;
    const viewSplit = document.getElementById('viewSplit') as HTMLInputElement;
    const viewUnified = document.getElementById('viewUnified') as HTMLInputElement;

    function getViewMode(): 'split' | 'unified' {
        return viewUnified.checked ? 'unified' : 'split';
    }

    function runDiff(): void {
        const original = originalEl.value;
        const changed = changedEl.value;

        if (!original.trim() && !changed.trim()) {
            diffResults.style.display = 'none';
            statsBar.style.display = 'none';
            identicalState.style.display = 'none';
            emptyState.style.display = '';
            return;
        }

        const originalLines = original.split('\n');
        const changedLines = changed.split('\n');
        const diffs = computeDiff(originalLines, changedLines);
        const hasChanges = diffs.some(d => d.type !== 'equal');

        if (!hasChanges) {
            diffResults.style.display = 'none';
            statsBar.style.display = 'none';
            emptyState.style.display = 'none';
            identicalState.style.display = '';
            return;
        }

        const rows = buildSplitRows(diffs);

        // Stats
        const stats = computeStats(rows);
        statAdded.textContent = String(stats.added);
        statRemoved.textContent = String(stats.removed);
        statChanged.textContent = String(stats.changed);
        statsBar.style.display = '';

        // Render
        const mode = getViewMode();

        if (mode === 'split') {
            const [left, right] = renderSplit(rows);
            splitLeft.innerHTML = left;
            splitRight.innerHTML = right;
            splitView.style.display = '';
            unifiedView.style.display = 'none';
        } else {
            unifiedContent.innerHTML = renderUnified(diffs);
            unifiedView.style.display = '';
            splitView.style.display = 'none';
        }

        emptyState.style.display = 'none';
        identicalState.style.display = 'none';
        diffResults.style.display = '';
    }

    compareBtn.addEventListener('click', runDiff);

    clearBtn.addEventListener('click', () => {
        originalEl.value = '';
        changedEl.value = '';
        diffResults.style.display = 'none';
        statsBar.style.display = 'none';
        identicalState.style.display = 'none';
        emptyState.style.display = '';
    });

    viewSplit.addEventListener('change', () => { if (diffResults.style.display !== 'none') runDiff(); });
    viewUnified.addEventListener('change', () => { if (diffResults.style.display !== 'none') runDiff(); });
});
